import React, { useState, useEffect } from 'react';
import { Mail, Wallet, User } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://plexo-natural-api.onrender.com';

const ProfileCreation = ({ onProfileCreated }) => {
  const [step, setStep] = useState('method');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    bio: ''
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token && token !== 'undefined') {
      localStorage.setItem('token', token);
      axios.get(`${API_BASE_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        onProfileCreated(res.data.profile, token);
      }).catch(err => {
        console.error('Error loading profile:', err);
        localStorage.removeItem('token');
      });
    }
  }, [onProfileCreated]);

  const handleGoogleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${API_BASE_URL}/auth/google/callback`;
    
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=email%20profile` +
      `&prompt=select_account`;

    window.location.href = authUrl;
  };

  const handleMetaMaskLogin = async () => {
    if (typeof window.ethereum === 'undefined') {
      setError('MetaMask não instalado. Instale em metamask.io');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // ✅ CORREÇÃO: Limpar estado anterior antes de conectar
      localStorage.removeItem('walletAddress');
      localStorage.removeItem('userData');
      localStorage.removeItem('token');

      console.log('🔄 Solicitando conexão da MetaMask...');

      // ✅ CORREÇÃO: eth_requestAccounts SEMPRE pede para selecionar conta
      const accounts = await window.ethereum.request({ 
        method: 'eth_requestAccounts' 
      });
      
      if (accounts.length === 0) {
        throw new Error('Nenhuma conta selecionada!');
      }

      const walletAddress = accounts[0];
      console.log('🔗 Nova conta conectada:', walletAddress);

      const response = await axios.post(`${API_BASE_URL}/auth/wallet`, {
        walletAddress,
        name: '',
        bio: ''
      });

      console.log('📨 Resposta do servidor:', response.data);

      if (response.data.token && response.data.token !== 'undefined') {
        localStorage.setItem('walletAddress', walletAddress);
        localStorage.setItem('token', response.data.token);
        onProfileCreated(response.data.profile, response.data.token);
        
        // Limpar URL após sucesso
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        setError('Token não recebido do servidor');
      }

    } catch (error) {
      console.error('❌ Erro ao conectar MetaMask:', error);
      setError(error.response?.data?.error || error.message || 'Erro ao conectar carteira');
      
      // Limpar tudo em caso de erro
      localStorage.removeItem('walletAddress');
      localStorage.removeItem('userData');
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.name || !formData.bio) {
      setError('Preencha todos os campos');
      return;
    }

    if (formData.bio.length > 300) {
      setError('Bio deve ter no máximo 300 caracteres');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.post(`${API_BASE_URL}/auth/email`, formData);
      
      if (response.data.token && response.data.token !== 'undefined') {
        localStorage.setItem('token', response.data.token);
        onProfileCreated(response.data.profile, response.data.token);
      } else {
        setError('Token não recebido do servidor');
      }
      
    } catch (error) {
      setError(error.response?.data?.error || 'Erro ao criar perfil');
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'method') {
    return (
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm p-8 border">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Criar Perfil</h2>
          <p className="text-gray-600 mt-2">Escolha como entrar</p>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center space-x-2 bg-white border-2 border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>Continuar com Google</span>
          </button>

          <button
            onClick={handleMetaMaskLogin}
            disabled={loading}
            className="w-full flex items-center justify-center space-x-2 bg-orange-500 text-white py-3 px-4 rounded-lg hover:bg-orange-600 transition disabled:opacity-50"
          >
            <Wallet className="w-5 h-5" />
            <span>{loading ? 'Conectando...' : 'Conectar MetaMask'}</span>
          </button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">ou</span>
            </div>
          </div>

          <button
            onClick={() => setStep('email')}
            className="w-full flex items-center justify-center space-x-2 border-2 border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:border-gray-400 transition"
          >
            <Mail className="w-5 h-5" />
            <span>Continuar com Email</span>
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm p-8 border">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Criar Perfil</h2>
      </div>
      
      <form onSubmit={handleEmailSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bio ({formData.bio.length}/300)
          </label>
          <textarea
            required
            rows={4}
            maxLength={300}
            value={formData.bio}
            onChange={(e) => setFormData(prev => ({...prev, bio: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder="Conte um pouco sobre você..."
          />
        </div>
        
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}
        
        <div className="flex space-x-3 pt-2">
          <button
            type="button"
            onClick={() => setStep('method')}
            className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 transition"
          >
            Voltar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
          >
            {loading ? 'Criando...' : 'Criar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileCreation;
